import React from 'react';
import { AbsoluteFill, useCurrentFrame, useVideoConfig, interpolate, spring } from 'remotion';

interface FeatureItem {
  name: string;
  desc: string;
}

interface CSS2026FeaturesAnimationProps {
  title?: string;
  features?: FeatureItem[];
  footerText?: string;
  background?: string;
  springConfig?: { damping: number; stiffness: number; mass: number };
  particleCount?: number;
  titleFontSize?: number;
  cardBorderRadius?: number;
  containerPadding?: number;
}

const defaultFeatures: FeatureItem[] = [
  { name: ':playing/:paused', desc: 'Media state pseudo-classes' },
  { name: 'View Transitions API', desc: 'Native page transitions' },
  { name: 'Scroll-driven Animations', desc: 'animation-timeline: scroll()' },
  { name: '@property', desc: 'Custom property types' },
  { name: 'CSS Trigonometry', desc: 'sin(), cos(), tan()' },
];

// #393 CSS 新特性动画 (CSS 2026 Features Animation)
export const CSS2026FeaturesAnimation: React.FC<CSS2026FeaturesAnimationProps> = ({
  title = 'CSS 2026 Features',
  features = defaultFeatures,
  footerText = 'WebKit Interop 2026',
  background = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  springConfig = { damping: 20, stiffness: 100, mass: 0.5 },
  particleCount = 20,
  titleFontSize = 60,
  cardBorderRadius = 30,
  containerPadding = 60,
}) => {
  const frame = useCurrentFrame();
  const { width, height, fps } = useVideoConfig();

  return (
    <AbsoluteFill
      style={{
        background,
        padding: containerPadding,
      }}
    >
      <div
        style={{
          width: width - containerPadding * 2,
          height: height - containerPadding * 2,
          background: 'rgba(255,255,255,0.1)',
          backdropFilter: 'blur(20px)',
          borderRadius: cardBorderRadius,
          padding: 40,
          border: '2px solid rgba(255,255,255,0.2)',
          transform: `scale(${spring({ frame, fps, ...springConfig })})`,
          opacity: interpolate(frame, [0, 20], [0, 1], { extrapolateRight: 'clamp' }),
        }}
      >
        <div
          style={{
            fontSize: titleFontSize,
            fontWeight: 'bold',
            color: 'white',
            marginBottom: 40,
            opacity: interpolate(frame, [20, 40], [0, 1], { extrapolateRight: 'clamp' }),
            transform: `translateY(${interpolate(frame, [20, 40], [20, 0], { extrapolateRight: 'clamp' })}px)`,
          }}
        >
          {title}
        </div>

        {features.map((feature, i) => {
          const delay = 40 + i * 10;
          const progress = spring({ frame: frame - delay, fps, ...springConfig });

          return (
            <div
              key={i}
              style={{
                background: 'rgba(255,255,255,0.15)',
                padding: '20px 30px',
                borderRadius: cardBorderRadius / 2,
                marginBottom: 15,
                transform: `translateX(${interpolate(progress, [0, 1], [-100, 0])}px)`,
                opacity: progress,
              }}
            >
              <div style={{ fontSize: 30, fontWeight: 'bold', color: 'white' }}>
                {feature.name}
              </div>
              <div style={{ fontSize: 24, color: 'rgba(255,255,255,0.8)', marginTop: 5 }}>
                {feature.desc}
              </div>
            </div>
          );
        })}

        <div
          style={{
            position: 'absolute',
            bottom: containerPadding,
            right: containerPadding,
            fontSize: 26,
            color: 'rgba(255,255,255,0.6)',
            opacity: interpolate(frame, [90, 120], [0, 1], { extrapolateRight: 'clamp' }),
          }}
        >
          {footerText}
        </div>
      </div>

      {Array.from({ length: particleCount }).map((_, i) => {
        const angle = (i / particleCount) * Math.PI * 2 + frame * 0.02;
        const radius = 200 + Math.sin(frame * 0.05 + i) * 30;
        const x = width / 2 + Math.cos(angle) * radius;
        const y = height / 2 + Math.sin(angle) * radius;

        return (
          <div
            key={i}
            style={{
              position: 'absolute',
              left: x,
              top: y,
              width: 10,
              height: 10,
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.3)',
              filter: 'blur(2px)',
            }}
          />
        );
      })}
    </AbsoluteFill>
  );
};
